import React, { useEffect, useState } from 'react';
import '../csscomponent/Login.css';
import Lottie from 'lottie-react';
import loginImage from '../login.json';
import eyeOpen from '../assest/eye.png';
import eyeClosed from '../assest/show.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { resendFirebaseVerification } from '../lib/firebaseAuth';
import { useAuth } from '../lib/authSession';
import useSingleTapNavigate from '../lib/useSingleTapNavigate';
import LogoButtonLoader from './mobile/LogoButtonLoader';

const BUTTON_LOADER_MIN_MS = 150;

export default function Login() {
  const rememberStorageKey = 'sd_remember_login';
  const navigate = useNavigate();
  const location = useLocation();
  const { goTo, navLocked } = useSingleTapNavigate();
  const { login, loginGoogle } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    try {
      const remembered = localStorage.getItem(rememberStorageKey);
      if (!remembered) return;
      const parsed = JSON.parse(remembered);
      setEmail(parsed?.email || '');
      setPassword(parsed?.password || '');
      setRememberMe(Boolean(parsed?.email || parsed?.password));
    } catch (_) {}
  }, []);

  useEffect(() => {
    const state = location.state;
    if (!state || typeof state !== 'object') return;

    const nextEmail = typeof state.email === 'string' ? state.email : '';
    const nextMessage = typeof state.message === 'string' ? state.message : '';

    if (nextEmail) {
      setEmail(nextEmail);
    }
    if (nextMessage) {
      setAuthMessage(nextMessage);
    }
    if (state.needsVerification === true) {
      setNeedsVerification(true);
    }

    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const holdLoader = () => new Promise((resolve) => {
    window.setTimeout(resolve, BUTTON_LOADER_MIN_MS);
  });

  const validate = () => {
    const newErrors = { email: '', password: '' };

    if (!email.trim()) {
      newErrors.email = '⚠ Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = '⚠ Enter a valid email';
    }

    if (!password.trim()) {
      newErrors.password = '⚠ Password is required';
    }

    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAuthMessage('');
    setNeedsVerification(false);
    if (!validate()) return;

    try {
      setLoading(true);
      await Promise.all([
        login({ email: email.trim(), password }),
        holdLoader(),
      ]);
      if (rememberMe) {
        localStorage.setItem(rememberStorageKey, JSON.stringify({
          email: email.trim(),
          password,
        }));
      } else {
        localStorage.removeItem(rememberStorageKey);
      }
      const pendingCheckoutPath = sessionStorage.getItem('pendingCheckoutPath');
      if (pendingCheckoutPath) {
        sessionStorage.removeItem('pendingCheckoutPath');
        navigate(pendingCheckoutPath, { replace: true });
        return;
      }
      toast.success('Login successful.');
      navigate('/home', { replace: true });
    } catch (error) {
      const nextMessage = error?.message || 'Unable to sign in right now. Please try again.';
      const needsEmailVerification = error?.code === 'EMAIL_NOT_VERIFIED';
      setAuthMessage(needsEmailVerification ? nextMessage : '');
      setNeedsVerification(needsEmailVerification);
      toast.error(nextMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthMessage('');
    try {
      setGoogleLoading(true);
      await Promise.all([
        loginGoogle(),
        holdLoader(),
      ]);
      const pendingCheckoutPath = sessionStorage.getItem('pendingCheckoutPath');
      if (pendingCheckoutPath) {
        sessionStorage.removeItem('pendingCheckoutPath');
        navigate(pendingCheckoutPath, { replace: true });
        return;
      }
      toast.success('Signed in with Google successfully.');
      navigate('/home', { replace: true });
    } catch (error) {
      const msg = error?.message || 'Google sign in failed. Please try again.';
      setAuthMessage(msg);
      toast.error(msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendLoading(true);
      const result = await resendFirebaseVerification();
      const successMessage = result?.message || 'Verification email sent.';
      setAuthMessage(successMessage);
      setNeedsVerification(false);
      toast.success(successMessage);
    } catch (error) {
      const errorMessage = error?.message || 'Unable to resend verification email.';
      setAuthMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login">
        <div className="content">
          <div className="login-topbar">
            <button
              type="button"
              className="login-page-skip-left"
              onClick={() => goTo('/home')}
              title="Skip for now"
              disabled={navLocked}
            >
              Skip
            </button>

            <div className="login-topbar-actions">
              <button
                type="button"
                className="signup-btn"
                onClick={() => goTo('/signup')}
                title="Sign Up"
                disabled={navLocked}
              >
                <span>New here? Sign up</span>
              </button>
            </div>
          </div>

          <h1>Welcome to Services and Developer</h1>

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <div className={`login-input ${errors.email ? 'login-error-input' : ''}`}>
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
              />
              {errors.email && <p className="login-error">{errors.email}</p>}
            </div>

            {/* Password */}
            <div className={`login-input ${errors.password ? 'login-error-input' : ''}`}>
              <label>Password</label>
              <div className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                />
                <img
                  src={showPassword ? eyeOpen : eyeClosed}
                  alt="Toggle"
                  className="eye-icon"
                  onClick={() => setShowPassword(!showPassword)}
                />
              </div>

              {errors.password && <p className="login-error">{errors.password}</p>}
            </div>

            {/* Options */}
            <div className="options-row">
              <div className="rem">
                <input
                  type="checkbox"
                  id="remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="remember">Remember me</label>
              </div>
              <button
                type="button"
                className="forgot"
                onClick={() => goTo('/forgot-password')}
                disabled={navLocked || loading}
              >
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              className={`login-submit-btn ${loading ? 'btn-loading' : ''}`}
              disabled={loading || googleLoading}
            >
              {loading ? (
                <LogoButtonLoader text="Signing in..." />
              ) : (
                'Sign In'
              )}
            </button>

            <div className="login-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className={`login-google-btn ${googleLoading ? 'btn-loading' : ''}`}
              onClick={handleGoogleSignIn}
              disabled={loading || googleLoading}
              aria-label="Sign in with Google"
            >
              {googleLoading ? (
                <LogoButtonLoader text="Signing in with Google..." isDarkText />
              ) : (
                <>
                  <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                  <span>Sign in with Google</span>
                </>
              )}
            </button>

            {authMessage ? <p className="login-auth-message">{authMessage}</p> : null}
            {needsVerification ? (
              <button
                type="button"
                className="login-resend-btn"
                onClick={handleResendVerification}
                disabled={resendLoading}
              >
                {resendLoading ? (
                  <LogoButtonLoader text="Sending email..." isDarkText />
                ) : (
                  'Resend verification email'
                )}
              </button>
            ) : null}
          </form>
        </div>

        <div className="image">
          <Lottie animationData={loginImage} loop className="hero-lottie" />
        </div>
      </div>
    </div>
  );
}
